import { memo } from 'react';

import { useCurrencyFormatter, useNumberFormatter } from '@myn/mui';

import FormattedAutocomplete from '../../../../../../molecules/FormattedAutocomplete/FormattedAutocomplete.jsx';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import useSelectableValueField from '../hooks/useSelectableValueField.js';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import AutocompleteWrapper from '../wrappers/AutocompleteWrapper.jsx';

export type Props = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

// This const will never be used. It's needed for handling an error that appears when currency does not exist.
const FALLBACK_CURRENCY = 'USD';

const SelectCurrencyValue = memo(({ value, column, row, cellColor }: Props) => {
  const { currencyValue: currency, scale } = row.original;

  const {
    isFieldFocused,
    isReadOnlyField,
    isResetBtnShown,
    handleOpenMenu,
    handleCloseMenu,
    handleChangeValue,
    handleResetValue,
  } = useSelectableValueField({
    value,
    column,
    row,
  });

  const formatterOptions = {
    min: 0,
    minDecimals: column.decimals,
    maxDecimals: column.decimals,
  };
  const currencyFormatter = useCurrencyFormatter({
    currency: currency || FALLBACK_CURRENCY,
    options: formatterOptions,
  });
  const numberFormatter = useNumberFormatter({ options: formatterOptions });

  const formatter = currency ? currencyFormatter : numberFormatter;

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        currency={currency}
        value={value}
        decimalLimit={column.decimals}
        variant="currency"
        color={cellColor}
      />
    );
  }

  if (!isFieldFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={isResetBtnShown}
        deleteValueFunc={handleResetValue}
        showEditModeFunc={handleOpenMenu}
        startMeasurePerformance={column.startMeasurePerformance}
        currency={currency}
        value={value}
        variant="currency"
        decimalLimit={column.decimals}
      />
    );
  }

  return (
    <AutocompleteWrapper $scale={scale}>
      <FormattedAutocomplete
        value={value}
        options={column.options}
        onChange={handleChangeValue}
        onClose={handleCloseMenu}
        format={formatter.format}
      />
    </AutocompleteWrapper>
  );
});

export default SelectCurrencyValue;
